import { useEffect, useMemo } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Info } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { ApiError } from '@/api/client'
import { credentialNameProblem, providerLabel } from '@/lib/gitRouting'
import { GIT_PROVIDER_OPTIONS, type GitRoutingRule } from '@/types/gitRouting'

/** Far above any provider's token; the store refuses anything longer (`GitCredentialNames.MaxValueLength`). */
const MAX_TOKEN_LENGTH = 4096

export interface SetGitCredentialDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The credential being set or replaced; absent when the user names a new one. */
  name?: string
  /** Whether a token is already stored under `name`, which makes this a replacement. */
  replacing?: boolean
  /** The tenant rules that use this credential, for the "Used by" line. */
  usedBy?: GitRoutingRule[]
  /** Stores the token. `test` asks the page to check a matching URL afterwards. */
  onSubmit: (name: string, value: string, test: boolean) => Promise<void>
}

const schema = z
  .object({
    name: z.string().trim(),
    value: z.string(),
    test: z.boolean(),
  })
  .superRefine((values, ctx) => {
    const provider = values.name.split(':')[0]
    const known = GIT_PROVIDER_OPTIONS.some((p) => p.value === provider)
    const problem = known
      ? credentialNameProblem(values.name, provider)
      : 'Name it provider:name, e.g. github:acme — the provider is github, gitea or ado.'
    if (problem) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['name'], message: problem })
    }

    if (values.value.trim() === '') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['value'], message: 'Paste the token to store.' })
    } else if (values.value.length > MAX_TOKEN_LENGTH) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['value'],
        message: `A token cannot be longer than ${MAX_TOKEN_LENGTH} characters.`,
      })
    }
  })

type FormValues = z.infer<typeof schema>

/**
 * Set or replace one of the tenant's Git tokens (GR3). Write-only: the input is a password field, never
 * prefilled, and the value is cleared whenever the dialog closes — it is never shown back.
 */
export function SetGitCredentialDialog({
  open,
  onOpenChange,
  name,
  replacing,
  usedBy = [],
  onSubmit,
}: SetGitCredentialDialogProps) {
  const empty = useMemo<FormValues>(() => ({ name: name ?? '', value: '', test: false }), [name])
  const form = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: empty })

  // Cleared on every open and every close, so the token lives no longer than the dialog does.
  useEffect(() => {
    form.reset(empty)
  }, [open, empty, form])

  const typedName = useWatch({ control: form.control, name: 'name' }) ?? ''
  const provider = (name ?? typedName).split(':')[0]
  const owner = (name ?? typedName).split(':')[1] ?? ''

  const close = () => {
    form.reset(empty)
    onOpenChange(false)
  }

  const submit = (test: boolean) =>
    form.handleSubmit(async (values) => {
      try {
        await onSubmit(values.name, values.value, test)
        close()
      } catch (error) {
        if (!(error instanceof ApiError)) {
          throw error
        }
        const nameError = error.fieldError('name')
        const valueError = error.fieldError('value')
        if (nameError) form.setError('name', { message: nameError })
        if (valueError) form.setError('value', { message: valueError })
        if (!nameError && !valueError) form.setError('root', { message: error.message })
      }
    })

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{name ? `${replacing ? 'Replace' : 'Set'} token: ${name}` : 'Set token'}</DialogTitle>
          <DialogDescription>
            The token is stored encrypted for this workspace only. It cannot be viewed again: to change it, replace it.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={submit(false)} className="space-y-4" noValidate>
            {!name && (
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input placeholder="github:acme" autoComplete="off" spellCheck={false} {...field} />
                    </FormControl>
                    <FormDescription>The name your routing rules refer to, provider:name.</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="value"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Token</FormLabel>
                  <FormControl>
                    <Input
                      type="password"
                      autoComplete="new-password"
                      spellCheck={false}
                      data-1p-ignore
                      data-lpignore="true"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <ProviderGuidance provider={provider} owner={owner} />

            <p className="flex items-start gap-2 text-sm text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              Nothing on this page can read a token back. The platform uses it only to reach the repositories your
              rules route to it.
            </p>

            {usedBy.length > 0 && (
              <p className="text-sm">
                Used by:{' '}
                {usedBy.map((r) => `rule ${r.position} (${r.urlPattern})`).join(', ')}
              </p>
            )}

            {form.formState.errors.root?.message && (
              <p role="alert" className="text-sm font-medium text-destructive">
                {form.formState.errors.root.message}
              </p>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={close}>
                Cancel
              </Button>
              <Button type="button" variant="outline" disabled={form.formState.isSubmitting} onClick={submit(true)}>
                Save and test
              </Button>
              <Button type="submit" disabled={form.formState.isSubmitting}>
                {form.formState.isSubmitting ? 'Saving…' : 'Save token'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

/** What kind of token each provider needs, so the first one set is the right one. */
function ProviderGuidance({ provider, owner }: { provider: string; owner: string }) {
  const subject = owner && owner !== 'default' ? `"${owner}"` : 'the repositories’ owner'

  switch (provider) {
    case 'github':
      return (
        <p className="text-sm text-muted-foreground">
          GitHub fine-grained tokens have one resource owner. Create this one with {subject} as the owner and read
          access to Contents, Pull requests and Issues (write to Pull requests to post reviews).
        </p>
      )
    case 'gitea':
      return (
        <p className="text-sm text-muted-foreground">
          Create a Gitea access token with read access to repositories and issues, and write access to issues to
          post review comments.
        </p>
      )
    case 'ado':
      return (
        <p className="text-sm text-muted-foreground">
          Create an Azure DevOps personal access token in {owner.startsWith('org-') ? `the "${owner.slice(4)}" organisation` : 'the organisation'} with
          Code (Read) and Pull Request Threads (Read &amp; write).
        </p>
      )
    default:
      return provider ? (
        <p className="text-sm text-muted-foreground">A token for {providerLabel(provider)}.</p>
      ) : null
  }
}
